import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Server,
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Database,
  Wifi,
  HardDrive,
  Loader2,
  Play,
  Terminal
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const AdminSystem = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/system-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (e) {
      toast.error('Error al obtener estado del sistema');
    } finally {
      setLoading(false);
    }
  };

  const runScraper = async () => {
    setRunningJob('scraper');
    setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: 'Iniciando scraper...', type: 'info' }]);
    
    try {
      const res = await axios.post(`${API}/admin/run-scraper`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `Scraper completado: ${res.data.results_updated || 0} resultados actualizados`, type: 'success' }]);
      toast.success('Scraper ejecutado correctamente');
      fetchStatus();
    } catch (e) {
      setLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), message: `Error: ${e.response?.data?.detail || 'Error desconocido'}`, type: 'error' }]);
      toast.error('Error al ejecutar scraper');
    } finally {
      setRunningJob(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Estado del Sistema" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Estado del Sistema" subtitle="Monitoreo y mantenimiento">
      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status?.api_status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <Wifi size={20} className={status?.api_status === 'online' ? 'text-green-400' : 'text-red-400'} />
              </div>
              <div>
                <p className="text-lg font-bold text-white">API</p>
                <Badge className={status?.api_status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {status?.api_status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status?.database_status === 'connected' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <Database size={20} className={status?.database_status === 'connected' ? 'text-green-400' : 'text-red-400'} />
              </div>
              <div>
                <p className="text-lg font-bold text-white">Database</p>
                <Badge className={status?.database_status === 'connected' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {status?.database_status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status?.scraper_status === 'running' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                <Activity size={20} className={status?.scraper_status === 'running' ? 'text-green-400' : 'text-yellow-400'} />
              </div>
              <div>
                <p className="text-lg font-bold text-white">Scraper</p>
                <Badge className={status?.scraper_status === 'running' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                  {status?.scraper_status?.toUpperCase() || 'IDLE'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Clock size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-lg font-bold text-white">Intervalo</p>
                <p className="text-gray-400 text-sm">5 minutos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Info & Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Server size={20} className="text-blue-400" />
              Información del Sistema
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-3 rounded-lg bg-gray-800/50">
              <span className="text-gray-400">Último Scrape</span>
              <span className="text-white font-mono text-sm">
                {status?.last_scrape ? new Date(status.last_scrape).toLocaleString('es-DO') : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-gray-800/50">
              <span className="text-gray-400">Loterías Activas</span>
              <span className="text-white font-bold">{status?.active_lotteries || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-gray-800/50">
              <span className="text-gray-400">Resultados Hoy</span>
              <span className="text-white font-bold">{status?.results_today || 0}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-gray-800/50">
              <span className="text-gray-400">Total Resultados</span>
              <span className="text-white font-bold">{status?.total_results || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Play size={20} className="text-green-400" />
              Acciones Manuales
            </CardTitle>
            <CardDescription className="text-gray-500">
              Ejecuta tareas de mantenimiento manualmente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={runScraper}
              disabled={runningJob === 'scraper'}
              className="w-full bg-green-600 hover:bg-green-500 h-12"
            >
              {runningJob === 'scraper' ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  Ejecutando Scraper...
                </>
              ) : (
                <>
                  <RefreshCw size={18} className="mr-2" />
                  Ejecutar Scraper Ahora
                </>
              )}
            </Button>
            
            <Button 
              onClick={fetchStatus}
              variant="outline"
              className="w-full border-gray-700 text-gray-300 hover:bg-gray-800 h-12"
            >
              <Activity size={18} className="mr-2" />
              Actualizar Estado
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Terminal size={20} className="text-purple-400" />
            Logs Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-950 rounded-lg p-4 font-mono text-sm max-h-64 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-500">No hay logs recientes. Ejecuta una acción para ver los logs.</p>
            ) : (
              logs.map((log, idx) => (
                <div key={log.id || `log-${idx}`} className="flex gap-3 py-1">
                  <span className="text-gray-500">{log.time}</span>
                  <span className={
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'success' ? 'text-green-400' :
                    'text-blue-400'
                  }>
                    {log.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminSystem;
